import { Button, Chip, InputAdornment, OutlinedInput, Stack, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { userService } from "../../../../Core/Api/services";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import ModalMailingList, {
    ModalMailingListElementProps,
} from "../../../../Components/Modals/ModalMailingList/modal-mailing-list";
import { DbMailingListInfos } from "../../../../Core/Api/Types/db-mailing-list-infos";
import { useTranslation } from "react-i18next";
import { DeleteOutline } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsMailingListManagement() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = React.useState(true);
    const [trigger, setTrigger] = React.useState<boolean>(false);
    const enqueueCustomSnackbar = useCustomSnackbar();

    const [mailingLists, setMailingLists] = React.useState<DbMailingListInfos[]>([]);

    // Modal
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    React.useEffect(() => {
        userService.GetDetailedMailingList().then((lists) => {
            if (lists) {
                setMailingLists(lists);
                setIsLoading(false);
            }
        });
    }, [trigger]);

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: t("common:name"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: Row) => (
                <Typography title={data.value} variant="body2">
                    {data.value || "-"}
                </Typography>
            ),
        },
        {
            field: "emailCount",
            headerName: t("table:headerName.emailsCount"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: Row) => <Chip label={data?.row?.emails.length} color="info" size="small" />,
        },
        {
            headerName: t("table:headerName.mailAddresses"),
            field: "emails",
            flex: CellSize.xxlarge,
            minWidth: CellSize.xxlarge,
            renderCell: (data: Row) => {
                return (
                    <Stack gap={1} direction="row" justifyContent="flex-start" flexWrap={"wrap"}>
                        {data.value.map((email) => (
                            <Chip label={email} color="default" size="small" />
                        ))}
                    </Stack>
                );
            },
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            flex: CellSize.actionsSmall,
            minWidth: CellSize.actionsSmall,
            align: "right",
            getActions: (params: GridRowParams) =>
                !params || !params.row
                    ? []
                    : [
                        <GridActionsCellItem
                            icon={<EditIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
                            label={t("common:edit")}
                            title={t("common:editUpperCase")}
                            disabled={!UserHasAcl(AclTypes.aclBackofficeMailinglistCreate)}
                            onClick={() => {
                                if (!openModal) return;

                                openModal<ModalMailingListElementProps>(ModalMailingList, {
                                    context: "UPDATE",
                                    ml: params.row,
                                    onUpdate: () => setTrigger((prevState) => !prevState),
                                });
                            }}
                        />,
                        <GridActionsCellItem
                            icon={<DeleteOutline sx={{ width: 20, height: 20, color: "error.main" }} />}
                            label={t("common:delete")}
                            title={t("common:delete")}
                            onClick={() => {
                                if (!openModal) return;

                                openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
                                    displayComment: true,
                                    commentLabel: t("modal:comment.confirmDeleteMailingList"),
                                    title: t("modal:title.deleteMailingList"),
                                    onConfirm: () => {
                                        userService.DeleteMailingList(params?.row?.id).then(() => {
                                            enqueueCustomSnackbar(t("alert:success.mailingListDeleted"), "success");
                                            setTrigger((prevState) => !prevState);
                                        });
                                    },
                                });
                            }}
                        />,
                    ],
        },
    ];
    // #endregion

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disabled={!UserHasAcl(AclTypes.aclBackofficeMailinglistCreate)}
            onClick={() => {
                if (!openModal) return;

                openModal<ModalMailingListElementProps>(ModalMailingList, {
                    context: "CREATE",
                    onUpdate: () => setTrigger((prevState) => !prevState),
                });
            }}
        >
            {t("button:newMailingList")}
        </Button>
    );

    return (

        <DataTable
            loading={isLoading}
            tableKey={TableUuids.SETTINGS_MAILING_LISTS}
            columns={columns}
            rows={mailingLists}
            pagination={true}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            additionalActions={additionalActions}
        />
    );
}

export default AdminSettingsMailingListManagement;
